<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { useConfirmDelete } from '@/composables/useConfirmDelete'
import { useFormatDate } from '@/composables/useFormatDate'
import AppDialog from '@/components/ui/AppDialog.vue'

const props = defineProps({ groupId: { type: Number, required: true } })
const { t }  = useI18n()
const user   = useUserStore()
const notify = useNotify()
const { confirmDelete } = useConfirmDelete()
const { fmtDateShort }  = useFormatDate()

const locations   = ref([])
const loading     = ref(false)
const error       = ref('')
const showOffline = ref(false)
const showDialog  = ref(false)
const saving      = ref(false)

const form = ref({ fqdn: '', title: '' })

const canEdit = computed(() => user.can('hackwatch_groups.change'))

const offlineCount = computed(() => locations.value.filter(l => l.available === false).length)

const visibleLocations = computed(() =>
  showOffline.value
    ? locations.value
    : locations.value.filter(l => l.available !== false)
)

const isDuplicate = computed(() => {
  const url = form.value.fqdn.trim().toLowerCase()
  if (!url) return false
  return locations.value.some(l => (l.fqdn || '').toLowerCase() === url)
})

const columns = computed(() => {
  const base = [
    { field: 'available', header: t('hackwatch.groupDetail.locColStatus'), sortable: true  },
    { field: 'fqdn',      header: t('hackwatch.groupDetail.locColUrl'),    sortable: false },
    { field: 'title',     header: t('hackwatch.groupDetail.locColTitle'),  sortable: false },
  ]
  if (canEdit.value) base.push({ field: '_del', header: '', sortable: false })
  return base
})

function statusClass(loc) {
  if (loc.available === true)  return 'online'
  if (loc.available === false) return 'offline'
  return 'unknown'
}

function statusLabel(loc) {
  if (loc.available === true)  return t('hackwatch.groupDetail.locStatusOnline')
  if (loc.available === false) return t('hackwatch.groupDetail.locStatusOffline')
  return t('hackwatch.groupDetail.locStatusUnknown')
}

function statusIcon(loc) {
  if (loc.available === true)  return 'pi pi-circle-fill'
  if (loc.available === false) return 'pi pi-circle'
  return 'pi pi-clock'
}

function scrapeTooltip(loc) {
  const date = loc.lastscrape || loc.created_at
  if (!date) return null
  return t('hackwatch.groupDetail.locColLastScrape') + ': ' + fmtDateShort(date)
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.getHackerGroupLocations(props.groupId)
    locations.value = Array.isArray(data) ? data : []
  } catch {
    error.value = t('hackwatch.groupDetail.locationsError')
  } finally {
    loading.value = false
  }
}

function openDialog() {
  form.value = { fqdn: '', title: '' }
  showDialog.value = true
}

async function save() {
  const fqdn = form.value.fqdn.trim()
  if (!fqdn || isDuplicate.value) return
  saving.value = true
  try {
    await api.createHackerGroupLocation(props.groupId, {
      fqdn,
      title: form.value.title.trim() || null
    })
    notify.success(t('hackwatch.groupDetail.locAdded'))
    showDialog.value = false
    await load()
  } catch (e) {
    if (e?.response?.status === 409) {
      notify.error(t('hackwatch.groupDetail.locDuplicate'))
    } else {
      notify.error(t('hackwatch.groupDetail.locAddFailed'))
    }
  } finally {
    saving.value = false
  }
}

async function remove(loc) {
  if (!await confirmDelete(t('hackwatch.groupDetail.locDeleteConfirm'))) return
  try {
    await api.deleteHackerGroupLocation(props.groupId, loc.location_id)
    notify.success(t('hackwatch.groupDetail.locDeleted'))
    await load()
  } catch {
    notify.error(t('hackwatch.groupDetail.locDeleteFailed'))
  }
}

onMounted(load)
watch(() => props.groupId, load)
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-map-marker hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.groupDetail.legendLocations') }}</span>
      <pButton
        v-if="offlineCount > 0"
        :icon="showOffline ? 'pi pi-eye-slash' : 'pi pi-eye'"
        :label="showOffline ? t('hackwatch.groupDetail.locHideOffline') : t('hackwatch.groupDetail.locShowOffline').replace('{count}', offlineCount)"
        size="small"
        severity="secondary"
        outlined
        @click="showOffline = !showOffline"
      />
      <pButton
        v-if="canEdit"
        icon="pi pi-plus"
        :label="t('hackwatch.groupDetail.locAdd')"
        size="small"
        severity="secondary"
        outlined
        @click="openDialog"
      />
    </div>

    <AppTableWidget
      :columns="columns"
      :data="visibleLocations"
      :loading="loading"
      :paginated="true"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      :searchable="true"
      data-key="location_id"
      :empty-text="t('hackwatch.groupDetail.locationsEmpty')"
      @refresh="load"
    >
      <template #cell-available="{ row }">
        <span
          class="status-badge"
          :class="statusClass(row)"
          v-tooltip.top="scrapeTooltip(row)"
        >
          <i :class="statusIcon(row)" />
          {{ statusLabel(row) }}
        </span>
      </template>

      <template #cell-fqdn="{ row }">
        <a
          :href="row.slug || row.fqdn"
          target="_blank"
          rel="noopener"
          class="loc-link"
          :class="{ muted: row.available === false }"
        >
          <i class="pi pi-external-link mr-1" />{{ row.fqdn || row.slug || '—' }}
        </a>
      </template>

      <template #cell-title="{ row }">
        <span class="loc-title">{{ row.title || '—' }}</span>
      </template>

      <template #cell-_del="{ row }">
        <pButton
          icon="pi pi-trash"
          text
          rounded
          size="small"
          severity="danger"
          v-tooltip.top="t('common.delete')"
          @click="remove(row)"
        />
      </template>
    </AppTableWidget>

    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>

    <AppDialog
      v-model="showDialog"
      :title="t('hackwatch.groupDetail.locAddDialogTitle')"
      size="md"
      :show-footer="true"
      :loading="saving"
      @submit="save"
      @cancel="showDialog = false"
    >
      <div class="dialog-form">
        <div class="form-field required">
          <label>{{ t('hackwatch.groupDetail.locFqdnLabel') }}</label>
          <InputText
            v-model="form.fqdn"
            :placeholder="t('hackwatch.groupDetail.locFqdnPlaceholder')"
            :invalid="isDuplicate"
            class="w-full"
            autofocus
          />
          <small v-if="isDuplicate" class="duplicate-hint">
            <i class="pi pi-exclamation-triangle mr-1" />{{ t('hackwatch.groupDetail.locDuplicate') }}
          </small>
        </div>
        <div class="form-field">
          <label>{{ t('hackwatch.groupDetail.locTitleLabel') }}</label>
          <InputText
            v-model="form.title"
            :placeholder="t('hackwatch.groupDetail.locTitlePlaceholder')"
            class="w-full"
          />
        </div>
      </div>
      <template #footer>
        <pButton :label="t('common.cancel')" severity="secondary" text @click="showDialog = false" />
        <pButton
          :label="t('common.save')"
          :disabled="isDuplicate || !form.fqdn.trim()"
          :loading="saving"
          @click="save"
        />
      </template>
    </AppDialog>
  </div>
</template>

<style scoped>
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: default;
}
.status-badge.online  { color: var(--p-green-500, #22c55e); }
.status-badge.offline { color: var(--p-red-400,   #f87171); opacity: 0.75; }
.status-badge.unknown { color: var(--p-text-color-secondary); }

.loc-link {
  color: var(--p-primary-color);
  text-decoration: none;
  word-break: break-all;
  font-size: 0.82rem;
}
.loc-link:hover { text-decoration: underline; }
.loc-link.muted { opacity: 0.55; }

.loc-title {
  font-size: 0.82rem;
  color: var(--p-text-color-secondary);
}

.duplicate-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.78rem;
  color: var(--p-danger-color, var(--p-red-500));
}
</style>
